/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description useRecordPaymentForm
 */

import { useCallback, useEffect, useMemo } from 'react';
import { createForm, Form, onFieldInputValueChange } from '@formily/core';
import getPaymentRecordSchema, { schemaKeys } from 'components/forms/schema/paymentRecordSchema';
import _ from 'lodash';
import { PAYMENT_METHODS } from 'app-constants/payments';

type IUseRecordPaymentFormOptions = {
	attachmentMode?: PatientPayment.PaymentRecordAttachmentMode;
};

type IReturnProps = {
	form: Form;
	schema: AnyType;
	initialModel: PatientPayment.IRecordPaymentFormModel;
};

const getInitialModel = (
	data?: PatientPayment.IParsedPaymentReconciliation,
	attachmentMode: PatientPayment.PaymentRecordAttachmentMode = 'legacy',
): PatientPayment.IRecordPaymentFormModel => {
	if (data) {
		return {
			amount: data?.paymentAmount,
			date: data?.collectedDate,
			paymentMethod: data?.paymentMethod,
			referenceId: data?.referenceId,
			description: data?.description,
			...(attachmentMode === 'legacy'
				? {
						files: _.map(data?.files, (file) => ({
							uid: file?.url,
							name: file?.name,
							url: file?.url,
						})),
					}
				: {}),
			selectedGuarantor: data?.paymentIssuer,
		};
	}
	return {
		amount: undefined,
		date: undefined,
		description: undefined,
		paymentMethod: undefined,
		selectedGuarantor: {},
		...(attachmentMode === 'legacy' ? { files: [] } : {}),
	} as PatientPayment.IRecordPaymentFormModel;
};

const useRecordPaymentForm = (
	parsedAllocationData?: PatientPayment.IParsedPaymentReconciliation,
	{ attachmentMode = 'legacy' }: IUseRecordPaymentFormOptions = {},
): IReturnProps => {
	const form = useMemo(() => createForm(), []);

	const syncReferenceIdValidation = useCallback(
		(paymentMethod?: string) => {
			const isCashPayment = _.toLower(paymentMethod) === _.toLower(PAYMENT_METHODS.cash.value);
			form.setFieldState(schemaKeys.referenceId, (state) => {
				state.required = !isCashPayment;
				if (isCashPayment) {
					state.selfErrors = [];
					state.feedbacks = [];
				}
			});
		},
		[form],
	);

	const { initialModel, schema } = useMemo(() => {
		return {
			initialModel: getInitialModel(parsedAllocationData, attachmentMode),
			schema: getPaymentRecordSchema({ attachmentMode }),
		};
	}, [attachmentMode, parsedAllocationData]);

	useEffect(() => {
		form.setInitialValues(initialModel);
		form.setValues(initialModel);
		syncReferenceIdValidation(initialModel[schemaKeys.paymentMethod]);
	}, [initialModel, form, syncReferenceIdValidation]);

	useEffect(() => {
		form.setEffects(() => {
			onFieldInputValueChange([schemaKeys.paymentMethod], (field) => {
				syncReferenceIdValidation(field?.value);
			});
		});
	}, [form, syncReferenceIdValidation]);

	return { form, schema, initialModel };
};

export default useRecordPaymentForm;
